import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { mockProjects, mockTasks, mockUsers } from '../mock/data';
import { FolderKanban, ListChecks, Clock, TrendingUp, CheckCircle2, Timer, AlertCircle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import HeaderGlow from '../components/HeaderGlow';

const Dashboard = () => {
  const navigate = useNavigate();
  const currentUser = mockUsers[0];
  const { currentColorTheme } = useTheme();

  const myTasks = mockTasks.filter(task => task.assigneeId === currentUser.id);
  const inProgressTasks = myTasks.filter(task => task.status === 'in-progress');
  const completedTasks = myTasks.filter(task => task.status === 'completed');
  const overdueTasks = myTasks.filter(task => new Date(task.dueDate) < new Date() && task.status !== 'completed');
  
  // Count escalated projects
  const escalatedProjects = mockProjects.filter(p => p.escalation?.isEscalated);

  const stats = [
    {
      title: 'Active Projects',
      value: mockProjects.length,
      icon: FolderKanban,
      gradient: 'from-blue-500 to-blue-600',
      change: '+2 this month',
      bgGradient: 'from-blue-500/10 to-blue-600/10'
    },
    {
      title: 'My Tasks',
      value: myTasks.length,
      icon: ListChecks,
      gradient: 'from-purple-500 to-purple-600',
      change: `${inProgressTasks.length} in progress`,
      bgGradient: 'from-purple-500/10 to-purple-600/10'
    },
    {
      title: 'My Escalations',
      value: escalatedProjects.length,
      icon: AlertTriangle,
      gradient: 'from-orange-500 to-orange-600',
      change: 'Needs attention',
      bgGradient: 'from-orange-500/10 to-orange-600/10'
    },
    {
      title: 'Completion Rate',
      value: `${Math.round((completedTasks.length / myTasks.length) * 100)}%`,
      icon: TrendingUp,
      gradient: 'from-green-500 to-green-600',
      change: '+5% from last week',
      bgGradient: 'from-green-500/10 to-green-600/10'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'in-progress': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
      case 'todo': return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';
      case 'low': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Dynamic Header - uses theme colors */}
      <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-blue-500 via-purple-500 to-pink-500'} p-8 shadow-lg`}>
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold text-white mb-2">Welcome back, {currentUser.name}! 👋</h1>
          <p className="text-white/80">Here's what's happening with your projects today.</p>
        </div>
      </div>
      {/* Glowing separator line */}
      <HeaderGlow />

      {/* Content */}
      <div className="max-w-7xl mx-auto p-8 space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className={`hover:shadow-xl transition-all duration-300 hover:scale-105 border-t-4 bg-gradient-to-br ${stat.bgGradient}`} style={{ borderTopColor: currentColorTheme?.accentColor || `hsl(var(--primary))` }}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                      <p className="text-3xl font-bold text-foreground mt-2">{stat.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.change}</p>
                    </div>
                    <div className={`p-4 rounded-xl bg-gradient-to-br ${currentColorTheme?.progressGradient || stat.gradient} shadow-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Active Projects */}
          <Card className="border-t-4" style={{ borderTopColor: currentColorTheme?.accentColor }}>
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-foreground mb-4 flex items-center">
                <FolderKanban className="w-5 h-5 mr-2" style={{ color: currentColorTheme?.accentColor }} />
                Active Projects
              </h3>
              <div className="space-y-4">
                {mockProjects.slice(0, 4).map((project) => (
                  <div
                    key={project.id}
                    className="p-4 rounded-xl border border-border hover:border-primary/50 transition-all cursor-pointer hover:shadow-md bg-gradient-to-r from-card to-muted/30"
                    onClick={() => navigate(`/projects/${project.id}/details`)}
                    style={{ '--hover-border-color': currentColorTheme?.accentColor }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color }}></div>
                        <h4 className="font-semibold text-foreground">{project.name}</h4>
                      </div>
                      <span className="text-sm font-semibold" style={{ color: currentColorTheme?.accentColor }}>{project.progress}%</span>
                    </div>
                    <Progress 
                      value={project.progress} 
                      className="h-2 mb-3" 
                      indicatorClassName={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'}`}
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex -space-x-2">
                        {project.teamMembers.slice(0, 3).map((memberId) => {
                          const member = mockUsers.find(u => u.id === memberId);
                          return (
                            <Avatar key={memberId} className="w-7 h-7 border-2 border-background">
                              <AvatarImage src={member?.avatar} />
                              <AvatarFallback>{member?.name.charAt(0)}</AvatarFallback>
                            </Avatar>
                          );
                        })}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Due {new Date(project.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* My Tasks */}
          <Card className="border-t-4" style={{ borderTopColor: currentColorTheme?.accentColorSecondary }}>
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-foreground mb-4 flex items-center">
                <ListChecks className="w-5 h-5 mr-2" style={{ color: currentColorTheme?.accentColorSecondary }} />
                My Tasks
              </h3>
              <div className="space-y-3">
                {myTasks.slice(0, 6).map((task) => {
                  const project = mockProjects.find(p => p.id === task.projectId);
                  return (
                    <div
                      key={task.id}
                      className="p-4 rounded-xl border border-border hover:border-primary/50 transition-all cursor-pointer hover:shadow-md"
                      onClick={() => navigate(`/my-tasks`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-medium text-foreground">{task.title}</h5>
                          <p className="text-sm text-muted-foreground mt-1">{project?.name}</p>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          <Badge className={getStatusColor(task.status)}>
                            {task.status}
                          </Badge>
                          <Badge className={getPriorityColor(task.priority)}>
                            {task.priority}
                          </Badge>
                        </div>
                      </div>
                      {task.dueDate && (
                        <p className="text-xs text-muted-foreground mt-2 flex items-center">
                          <Timer className="w-3 h-3 mr-1" />
                          Due: {new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/10 border-green-500/30">
            <CardContent className="p-4 flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{completedTasks.length}</p>
                <p className="text-sm text-muted-foreground">Tasks Completed</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-blue-500/30">
            <CardContent className="p-4 flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                <Timer className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{inProgressTasks.length}</p>
                <p className="text-sm text-muted-foreground">In Progress</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-500/10 to-red-600/10 border-red-500/30">
            <CardContent className="p-4 flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-xl">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{overdueTasks.length}</p>
                <p className="text-sm text-muted-foreground">Overdue Tasks</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;